import type { CityConfig } from "@/pages/city-template/types";

export const ashlandOrConfig: CityConfig = {
  city: "Ashland",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "ashland-or",
  route: "/cities/ashland-or",
  title: "Emergency Passport Ashland OR | Expedited Passport Rogue Valley Oregon",
  metaDescription: "Emergency passport and expedited passport services in Ashland, OR — as fast as 24 hours. Fast Passport Center serves Ashland, the Rogue Valley, Jackson County, Talent, and Medford. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Ashland, Oregon",
  heroSubtitle: "Oregon Shakespeare Festival traveler with an expired passport? Last-minute Mexico trip from Medford Airport? Family cruise booked and passport outdated? Fast Passport Center helps Ashland residents get expedited passports as fast as 24 hours — entirely remotely, with no Seattle office visit. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Ashland%20Oregon%20charming%20downtown%20street%20scene%20nestled%20among%20the%20Siskiyou%20and%20Cascade%20mountains%2C%20Lithia%20Park%20with%20mature%20oak%20trees%20and%20Ashland%20Creek%20flowing%20through%2C%20warm%20golden%20October%20light%20illuminating%20historic%20brick%20buildings%20and%20colorful%20autumn%20foliage%2C%20Pacific%20Northwest%20small%20town%20beauty%20with%20mountain%20views%20in%20background%2C%20vibrant%20and%20photorealistic%2C%20no%20text%20no%20people&width=1800&height=700&seq=ashland-or-hero-01&orientation=landscape",
  heroImageAlt: "Ashland Oregon downtown with Lithia Park and the Siskiyou Mountains",
  geoPosition: "42.1946;-122.7094",
  geoPlacename: "Ashland, Oregon",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 500 miles north — approximately 8 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Ashland", zips: "97520" },
    { name: "Talent", zips: "97540" },
    { name: "Phoenix", zips: "97535" },
    { name: "Medford", zips: "97501–97504" },
    { name: "Jacksonville", zips: "97530" },
    { name: "Rogue River", zips: "97537" },
    { name: "Central Point", zips: "97502" },
    { name: "White City", zips: "97503" },
    { name: "Eagle Point", zips: "97524" },
    { name: "Grants Pass", zips: "97526, 97527" },
    { name: "Klamath Falls", zips: "97601, 97603" },
    { name: "Yreka (CA)", zips: "96097" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Ashland", desc: "Complete your emergency or expedited passport order from Ashland — no drive to Seattle or Portland required." },
    { icon: "ri-truck-line", title: "Ship Overnight from Ashland or Medford", desc: "FedEx and UPS locations in Ashland and Medford serve the Rogue Valley for overnight passport document shipping." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — essential for southern Oregon residents far from passport agencies." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment system with reserved Seattle Passport Agency slots — no 8-hour drive required." },
  ],
  testimonials: [
    {
      name: "Jennifer K.",
      location: "Ashland, OR",
      rating: 5,
      text: "I discovered my passport expired three weeks before the Oregon Shakespeare Festival trip I'd been planning to extend into a Europe theatre tour. Fast Passport Center renewed it in 8 days. Saved a once-in-a-lifetime trip.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "David R.",
      location: "Ashland, OR",
      rating: 5,
      text: "Southern Oregon University professor — needed my passport renewed for an academic conference in London. Fast Passport Center handled it while I was still in the middle of finals week. Shipped from the Medford FedEx. Passport back in 7 days. Phenomenal service.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Patricia W.",
      location: "Talent, OR",
      rating: 5,
      text: "Our family had a cruise out of Vancouver BC booked and my husband's passport expired. Four weeks out — Fast Passport Center renewed it in 9 days. They were clear, responsive, and made a stressful situation manageable. Highly recommend to anyone in the Rogue Valley.",
      service: "Expedited Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "ashland-or-post-main",
      name: "Ashland Post Office",
      type: "USPS Passport Acceptance",
      address: "120 N First St",
      city: "Ashland",
      state: "OR",
      zip: "97520",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Ashland — primary passport acceptance location for Ashland residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Ashland residents. Appointment required — book well in advance during Oregon Shakespeare Festival season (spring through fall).",
      icon: "ri-mail-line",
    },
    {
      id: "jackson-county-clerk",
      name: "Jackson County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "10 S Oakdale Ave",
      city: "Medford",
      state: "OR",
      zip: "97501",
      phone: "(541) 774-6152",
      hours: "Mon–Fri 8am–5pm",
      distance: "Medford — about 14 miles north, Jackson County Courthouse",
      website: "https://www.jacksoncountyor.gov/clerk",
      websiteLabel: "Jackson County Clerk",
      notes: "Jackson County Clerk in Medford accepts passport applications. Generally better appointment availability than Ashland Post Office.",
      icon: "ri-government-line",
    },
    {
      id: "talent-post",
      name: "Talent Post Office",
      type: "USPS Passport Acceptance",
      address: "107 Colver Rd",
      city: "Talent",
      state: "OR",
      zip: "97540",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Talent — about 5 miles north of Ashland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Talent post office for Talent and Bear Creek area residents. Limited appointment availability — Ashland or Medford preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "medford-post-main",
      name: "Medford Main Post Office",
      type: "USPS Passport Acceptance",
      address: "3045 E Barnett Rd",
      city: "Medford",
      state: "OR",
      zip: "97504",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 10am–2pm",
      distance: "Medford — about 14 miles north, largest acceptance facility in southern Oregon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Medford's main post office is the largest passport acceptance facility in southern Oregon — significantly better appointment availability for Ashland residents. Saturday morning passport hours available.",
      icon: "ri-mail-line",
    },
    {
      id: "phoenix-post",
      name: "Phoenix Post Office",
      type: "USPS Passport Acceptance",
      address: "315 N Main St",
      city: "Phoenix",
      state: "OR",
      zip: "97535",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Phoenix — about 8 miles north of Ashland between Talent and Medford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Phoenix post office for Bear Creek Valley residents. Limited availability.",
      icon: "ri-mail-line",
    },
    {
      id: "jacksonville-post",
      name: "Jacksonville Post Office",
      type: "USPS Passport Acceptance",
      address: "175 W California St",
      city: "Jacksonville",
      state: "OR",
      zip: "97530",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Jacksonville — about 17 miles northwest of Ashland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Jacksonville historic district post office. Very limited appointment availability — Ashland or Medford strongly preferred.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Ashland, Oregon?",
      a: "Yes. Through Fast Passport Center, Ashland residents can get an emergency passport in as fast as 24 hours for urgent travel, or 2–9 business days with expedited processing. The nearest regional passport agency (Seattle) is 8 hours away — we serve Ashland entirely remotely. Start online, ship overnight from Ashland or the Medford FedEx, and we submit through reserved Seattle agency channels.",
    },
    {
      q: "How fast can I renew my passport from Ashland, OR?",
      a: "Expedited passport renewal from Ashland is available in as little as 3 business days through Fast Passport Center. Most adult renewals do not require an in-person acceptance visit. Ship overnight from the Ashland UPS Store or Medford FedEx Office — we handle the agency submission from there.",
    },
    {
      q: "Does Ashland, OR have a passport agency or acceptance office?",
      a: "Ashland has a post office (120 N First St) that accepts passport applications. The Jackson County Clerk in Medford (14 miles north) also accepts applications with better availability. The nearest regional passport agency is in Seattle — 500 miles away. Fast Passport Center bypasses the need for a Seattle trip through reserved agency submission.",
    },
    {
      q: "Where is the best passport acceptance facility near Ashland, Oregon?",
      a: "For Ashland residents, options include the Ashland Post Office (downtown), the Medford Main Post Office (14 miles north with Saturday hours and better availability), and the Jackson County Clerk in Medford. Medford's larger facility is recommended when Ashland appointments are backed up.",
    },
    {
      q: "Can I expedite a passport for a Mexico trip booked from the Rogue Valley?",
      a: "Yes. Mexico beach trips are common among Ashland and Rogue Valley residents — Puerto Vallarta, Cabo, and the Riviera Maya are popular winter and spring break destinations. Fast Passport Center handles expedited renewals as fast as 3 business days for adult renewal-eligible applicants from Ashland.",
    },
    {
      q: "Do I need a passport for an Oregon Shakespeare Festival trip extended to Europe?",
      a: "Yes. If you're extending an Ashland theatre visit to European festivals, a valid U.S. passport is required. This is a common Ashland travel pattern — using the OSF visit as a springboard for European theatre tours. Fast Passport Center handles expedited passports for Ashland cultural travelers.",
    },
    {
      q: "What airport do Ashland residents use for international travel?",
      a: "Rogue Valley International–Medford Airport (MFR, 14 miles north) provides regional connections to Portland, San Francisco, and Seattle for onward international flights. For direct transatlantic or transpacific service, Portland International (PDX, 280 miles north) is the best option. A valid U.S. passport is required for all international departures.",
    },
    {
      q: "Can I renew a passport without visiting an office in Ashland?",
      a: "Yes. Most adult Ashland residents renewing a passport can do so entirely by mail — no in-person acceptance visit required. Start online with Fast Passport Center, complete documents at home, and ship overnight from the Ashland UPS Store or Medford FedEx. No in-person visit to our processing facility required.",
    },
    {
      q: "How long does standard passport processing take from Ashland?",
      a: "Standard USPS mail processing from Ashland runs 6–13 weeks during normal periods, and longer during peak Oregon Shakespeare Festival season (April–October). If your trip is within 10 weeks, expedited service through Fast Passport Center is strongly recommended.",
    },
    {
      q: "Can Ashland Southern Oregon University students get expedited passports?",
      a: "Yes. Fast Passport Center regularly helps Southern Oregon University students and faculty in Ashland get expedited passports for study abroad programs, international academic conferences, and research travel. Start your order as soon as your travel is confirmed — study abroad programs typically have passport submission deadlines.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Ashland", address: "1780 Ashland St, Ashland, OR 97520", hours: "Daily 8 AM – 10 PM" },
    { name: "Rite Aid — Ashland", address: "720 Siskiyou Blvd, Ashland, OR 97520", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Medford South", address: "1550 Biddle Rd, Medford, OR 97504", hours: "Daily 7 AM – 11 PM" },
    { name: "FedEx Office — Medford", address: "924 N Central Ave, Medford, OR 97501", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Walgreens — Medford Crater Lake", address: "3200 Crater Lake Hwy, Medford, OR 97504", hours: "Daily 8 AM – 10 PM" },
    { name: "The UPS Store — Medford", address: "2 N Central Ave, Medford, OR 97501", hours: "Mon–Fri 8:30 AM – 6:30 PM, Sat 10 AM – 4 PM" },
    { name: "Costco Photo Center — Medford", address: "1255 Center Dr, Medford, OR 97501", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "CVS — Medford", address: "3523 Crater Lake Hwy, Medford, OR 97504", hours: "Daily 7 AM – 10 PM" },
    { name: "Office Depot — Medford", address: "2831 N Pacific Hwy, Medford, OR 97501", hours: "Mon–Sat 8 AM – 8 PM, Sun 10 AM – 6 PM" },
    { name: "Walmart — Grants Pass", address: "135 Grants Pass Pkwy, Grants Pass, OR 97527", hours: "Daily 7 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Ashland Driver & Motor Vehicle Services",
      address: "3523 Siskiyou Blvd, Ashland, OR 97520",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Ashland DMV — driver licenses, REAL ID, and ID cards for Ashland and Talent area residents.",
      featured: true,
    },
    {
      name: "Medford Driver & Motor Vehicle Services",
      address: "100 E Main St, Medford, OR 97501",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Medford DMV — larger office with better capacity for Jackson County residents needing REAL ID.",
      featured: false,
    },
    {
      name: "Grants Pass Driver & Motor Vehicle Services",
      address: "709 NW A St, Grants Pass, OR 97526",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Grants Pass DMV — alternative for Ashland area residents in western Jackson/Josephine County.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "MFR",
      name: "Rogue Valley International–Medford Airport",
      distance: "Primary airport — about 14 miles north of Ashland via I-5",
      address: "1000 Terminal Loop Pkwy, Medford, OR 97504",
      note: "MFR is the primary departure airport for Ashland travelers, with connections to Portland, San Francisco, Los Angeles, and Seattle. Most international travel requires a connecting flight. A valid U.S. passport is required for all international departures.",
      flightUrl: "https://www.jacksoncountyor.gov/airport",
      flightLabel: "MFR Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "About 280 miles north via I-5",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City — accessible for Ashland travelers willing to make the 4.5-hour drive north.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "About 320 miles south via I-5",
      address: "San Francisco, CA 94128",
      note: "SFO is an alternative major international hub for Ashland area travelers — accessible via a 5-hour drive south on I-5.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Ashland & the Rogue Valley",
      description: "Europe trip booked around the Oregon Shakespeare Festival? Last-minute Mexico escape from Medford? Fast Passport Center handles emergency passport processing for Ashland residents — as fast as 24 hours. Seattle is 8 hours away, but we serve you remotely.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired during the pandemic or years of OSF-focused domestic travel? Ashland residents renew fast through Fast Passport Center — ship from Ashland or Medford overnight, we handle the expedited agency submission.",
      bullets: ["Fastest renewal option for Ashland residents", "Ship overnight from Ashland or Medford", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Ashland or before an international trip? We guide Rogue Valley travelers through DS-64 and expedited replacement — no Seattle trip required.",
      bullets: ["DS-64 reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Ashland Families",
      description: "Ashland families traveling to Canada, Mexico, or internationally with children need child passports. We provide the checklist for the Ashland Post Office acceptance step — you handle the in-person visit, we expedite from there.",
      bullets: ["Both parents typically required to consent", "Ashland Post Office or Jackson County Clerk guidance", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First-time passport from Ashland? We guide you through the acceptance clerk step at the Ashland Post Office or Medford Jackson County Clerk and handle expedited processing after.",
      bullets: ["Step-by-step acceptance guidance", "Photo location guide for Ashland and Medford area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at prices 30–100% lower than FedEx Office or retail expediting services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Passport Services",
      description: "Name change after marriage, damaged passport replacement, second passports for Ashland academics, theatre professionals, and outdoor adventurers — all with expedited courier access.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passport for frequent academic travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Ashland, Oregon travelers with urgent international departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Ashland and Jackson County residents needing passports faster than standard USPS timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Ashland, Oregon residents — ship overnight from Ashland or Medford, we handle the rest." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Ashland travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Ashland and Rogue Valley families heading to British Columbia, Mexico, or international destinations." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission serving Ashland, Oregon and the Jackson County Rogue Valley area." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking International Travel Around OSF Season Without Checking Your Passport", description: "Ashland's Oregon Shakespeare Festival attracts visitors who often extend trips internationally — to European theatre festivals, UK Shakespeare tours, or Mexican culture trips. Many discover their passport expired only after booking. Check your passport the day you book your international extension." },
    { icon: "ri-camera-off-line", title: "Wrong or Rejected Passport Photo", description: "Photos from drugstores frequently get rejected for lighting or expression issues. Fast Passport Center reviews photo specifications before submission to prevent costly rejections." },
    { icon: "ri-file-damage-line", title: "Incomplete or Missing Signatures on Forms", description: "One missing signature returns your application — costing weeks. Our pre-submission review catches all form errors before they become delays." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are typically rejected. We specify exactly what document format you need based on your state of birth." },
    { icon: "ri-calendar-close-line", title: "SOU Students Applying Too Late for Study Abroad Deadlines", description: "Southern Oregon University study abroad programs have document submission deadlines months before departure. SOU students should start their passport applications as soon as study abroad acceptance is confirmed." },
    { icon: "ri-map-pin-time-line", title: "Ignoring the Six-Month Validity Rule", description: "Mexico, Canada (by air), and most European countries require your passport to be valid for six months beyond your return date. A passport expiring in three months is functionally expired for most international trips." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Ashland travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with Ashland and Medford area acceptance location recommendations." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Ashland or Medford", description: "Drop off at the Ashland UPS Store or Medford FedEx Office — both serve the Rogue Valley with overnight shipping." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — no waiting for public appointment cancellations or 8-hour drives." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped Directly to Ashland", description: "Your completed passport ships directly to your Ashland or Rogue Valley address. Track status in real time." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 8 hours each way",
        "Life-or-death emergencies only",
        "Very limited availability",
        "Not practical for Ashland OR residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship from Ashland or Medford overnight",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; longer during peak OSF season",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (8 hours each way from Ashland)", fpc: "Ship overnight from Ashland or Medford" },
    { feature: "Processing Speed", agency: "Strict eligibility — urgent travel only", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Southern Oregon Accessibility", agency: "Not designed for Rogue Valley residents", fpc: "Fully remote — ship from Ashland or Medford" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 16-hour Seattle round trip expense", fpc: "Best price guarantee — no travel cost" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Medford", route: "/cities/medford" },
    { name: "Emergency passport help in Grants Pass", route: "/cities/grants-pass" },
    { name: "Fast passport renewal in Klamath Falls", route: "/cities/klamath-falls" },
    { name: "Rush passport services in Roseburg", route: "/cities/roseburg" },
    { name: "Expedited passports for Oregon travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from the Ashland UPS Store or Medford FedEx Office — open evenings. No visit to our processing facility required for most services.",
  fpcOfficeDistance: "Serving Ashland and the entire Rogue Valley area remotely",
  serviceAreaIntro: "Whether you are in Ashland, Talent, Phoenix, Jacksonville, or anywhere in the Rogue Valley — Fast Passport Center makes emergency and expedited passport processing simple without the 8-hour drive to Seattle.",
  nearbyCitiesIntro: "We serve all Jackson County zip codes and surrounding communities including Talent, Phoenix, Jacksonville, Rogue River, Central Point, White City, Eagle Point, Medford, Grants Pass, and Klamath Falls.",
  howItWorksIntro: "Getting an emergency or expedited passport from Ashland is far simpler than an 8-hour drive to Seattle. Most Rogue Valley clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Ashland and Rogue Valley passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Ashland, Talent, and Rogue Valley travelers who needed their passports fast.",
  faqIntro: "The most common questions Ashland and Jackson County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Ashland and Rogue Valley Residents",
    intro: "Ashland is unlike any other city in southern Oregon — a cultural hub defined by the Oregon Shakespeare Festival, Southern Oregon University, and a cosmopolitan arts community in a rugged mountain setting. These identities create distinctive international travel patterns and passport urgency.",
    cards: [
      {
        icon: "ri-graduation-cap-line",
        title: "Southern Oregon University Study Abroad & Academic Travel",
        subtitle: "SOU international programs, academic conferences, and faculty research travel",
        description: "Southern Oregon University draws international students and sends students abroad through study programs in Europe, Asia, Latin America, and beyond. SOU faculty travel internationally for academic conferences, research partnerships, and visiting professor positions. Study abroad participants face strict document submission deadlines months before departure — missing them can disqualify a student from the program. Fast Passport Center regularly helps SOU students and faculty navigate expedited passport processing with program timeline awareness.",
        tip: "SOU study abroad students: the moment your program acceptance arrives, check your passport. Most study abroad programs require passport submission 3–6 months before departure. Don't wait for the program's document deadline to start.",
      },
      {
        icon: "ri-vidicon-line",
        title: "Oregon Shakespeare Festival — International Theatre Travel",
        subtitle: "OSF attracts arts audiences who extend trips to UK, Europe, and international theatre destinations",
        description: "The Oregon Shakespeare Festival is one of the world's largest Shakespeare festivals — attracting theatre-goers from across North America and internationally. A significant percentage of OSF visitors use the Ashland visit as an anchor for international theatre travel — extending to Edinburgh Festival Fringe, Stratford-upon-Avon, National Theatre London, or Paris. These trips are often planned months in advance but passport checks happen last-minute. Ashland arts travelers should confirm passport validity when booking OSF tickets that will extend internationally.",
        tip: "If you're planning to extend your OSF visit to Edinburgh Fringe (August), London West End, or European theatre festivals, check your passport validity 6+ months in advance. The Edinburgh Fringe is in August — book passports by May at the latest.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "Medford Airport (MFR) and Portland International (PDX)",
        subtitle: "14 miles to MFR; 280 miles to PDX for direct international flights",
        description: "Ashland residents use Medford Airport (MFR) for regional connections and Portland International (PDX) for direct international flights. MFR connects to Portland, San Francisco, Los Angeles, Denver, and Seattle — with onward international connections. PDX offers direct service to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City — about a 4.5-hour drive north. International departures from either airport require a valid U.S. passport.",
        tip: "For direct transatlantic or transpacific flights, Portland International (PDX) is worth the 4.5-hour drive from Ashland. Medford-to-Portland connecting itineraries often add 3–4 hours and an additional connection versus driving directly to PDX.",
      },
      {
        icon: "ri-sun-cloudy-line",
        title: "Mexico & Central America Winter & Spring Travel",
        subtitle: "Rogue Valley residents escape to Mexico and Costa Rica November through April",
        description: "Ashland winters are cold — and many Rogue Valley residents escape to Mexico, Costa Rica, and Central America from November through April. Puerto Vallarta, Cabo, Oaxaca, and the Riviera Maya are popular with Ashland arts and outdoor community. Costa Rica eco-adventure travel is common with Ashland's environmental and outdoor community. These winter travel bookings happen in September and October — often when passport expiration dates are discovered for the first time in years.",
        tip: "Book your Mexico or Costa Rica winter trip in September, then immediately check your passport. If it expires before May the following year, renew it now. Fast Passport Center expedites renewals in 3–9 business days from Ashland.",
      },
    ],
    callout: {
      icon: "ri-palette-line",
      title: "Ashland, Oregon — The Rogue Valley's Cultural Gateway to the World",
      description: "Ashland's identity is unique in southern Oregon — a university town, arts destination, and outdoor adventure hub combined. The OSF community, SOU international programs, and the Siskiyou outdoors create a diverse international travel profile unlike any other southern Oregon city. Fast Passport Center has helped students, faculty, theatre professionals, and outdoor adventurers in Ashland get their passports without a Seattle road trip.",
    },
  },
  destinations: {
    title: "Where Ashland Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Ashland and Rogue Valley clients. The combination of Oregon Shakespeare Festival, Southern Oregon University, and the Siskiyou outdoor community creates a distinctive and diverse set of international passport urgency situations.",
    stats: [
      { stat: "500 mi", label: "to Seattle Passport Agency — the nearest regional office from Ashland" },
      { stat: "14 mi", label: "to Medford Airport (MFR) — primary connection hub for Ashland international travel" },
      { stat: "Apr–Oct", label: "OSF peak season — when many Ashland international travellers discover passport expiration" },
      { stat: "24 Hrs", label: "fastest emergency passport processing available for Ashland residents" },
    ],
    destinations: [
      { name: "United Kingdom — London, Stratford, Edinburgh", context: "Theatre and arts travel from Ashland OSF community — West End productions, RSC at Stratford, Edinburgh Fringe Festival", urgency: "OSF visitors planning UK theatre extensions discover passport issues during trip planning in spring/summer", icon: "ri-global-line" },
      { name: "Mexico — Puerto Vallarta, Cabo, Oaxaca", context: "Warm winter escapes for Ashland arts and outdoor community — November through April beach and cultural travel", urgency: "October–November Mexico bookings; passports found expired during packing in December–January", icon: "ri-sun-cloudy-line" },
      { name: "British Columbia, Canada", context: "Skiing at Whistler, Vancouver city breaks, Okanagan wine country, and Gulf Islands ferry travel from Ashland", urgency: "BC ski season bookings in November; passport issues discovered at holiday document gathering time", icon: "ri-map-pin-2-line" },
      { name: "Costa Rica & Central America", context: "Eco-adventure travel — Ashland's environmental and outdoor community visits Manuel Antonio, Arenal, and Monteverde cloud forests", urgency: "Winter dry season travel booked in October–November; passport expiration discovered at booking time", icon: "ri-leaf-line" },
      { name: "Japan & East Asia", context: "SOU international students returning home, teaching English abroad from Ashland graduates, and cultural travel to Japan, Korea, and China", urgency: "Teaching program deadlines require passport months before departure; SOU students often discover issues at application time", icon: "ri-map-pin-2-line" },
      { name: "France & Western Europe", context: "Heritage travel for Ashland's French and European community; wine tourism to Burgundy and Bordeaux; arts travel to Paris and Avignon Festival", urgency: "Avignon Festival (July) and European wine harvest visits (September) have fixed dates — passport issues discovered 2–4 weeks before", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout southern Oregon with the same expedited passport expertise.",
    crossLinks: [
      { name: "Medford", route: "/cities/medford" },
      { name: "Grants Pass", route: "/cities/grants-pass" },
      { name: "Klamath Falls", route: "/cities/klamath-falls" },
    ],
  },
};